/**
 * PatientMergeDialog — picks the parent record from the selected
 * patients and confirms the bulk-merge.
 *
 * Flutter ref: `patient_merge_confirmation_popup_widget.dart`. The
 * Flutter version has a separate progress popup ("merging…") shown
 * during the network round-trip; here we keep the user inside this
 * dialog and surface the pending state on the Confirm button. Same
 * outcome — one less popup transition.
 *
 * Wire (Flutter `patient_list_bloc.dart`):
 *   POST /api/Patients/Merge
 *   body: { parentPatient: <id>, childPatients: [<ids excluding parent>] }
 *
 * On success the parent receives the children's data; the children get
 * the `isMerged` flag and disappear from the default list view (the
 * "Show merged" chip surfaces them again). The dialog's `onSuccess`
 * fires the navigate-to-parent + clear-selection side-effects.
 */

import { useEffect, useState } from 'react';
// GAP (design sheet §2.5): `GitMerge` has no Signal DS glyph yet (DS-4); it stays on lucide.
import { Button, Modal } from '@scripsteam/scrips-signal-ds';
import { ChoiceList } from '@/shared/ui/components/choice-list';
import type { PatientDatum } from '@/types/patient-api';

export interface PatientMergeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Selected patients passed in from the list page (length must be ≥ 2). */
  selected: PatientDatum[];
  /** Fires when the user clicks Confirm. Caller wires the mutation +
   *  the success-navigate + the selection clear. */
  onConfirm: (parentId: string, childIds: string[]) => void;
  isPending?: boolean;
}

export function PatientMergeDialog({
  open,
  onOpenChange,
  selected,
  onConfirm,
  isPending = false,
}: PatientMergeDialogProps) {
  const [parentId, setParentId] = useState<string | null>(null);
  // PROD-2743 design sheet §2.5 (I7): ON = each record's fact line reads person first and ends on
  // the MRN, in quiet meta. Same facts as today, reordered; OFF keeps today's MRN-first line.

  // Default to the first selected patient when the dialog opens / the
  // selection changes. Mirrors Flutter `_selectedPatientId =
  // widget.patients.first.patientId`.
  useEffect(() => {
    if (!open) return;
    if (selected.length === 0) {
      setParentId(null);
      return;
    }
    // Only reset when the previously-selected parent is no longer in the
    // list (e.g. user toggled selection between opens).
    if (!selected.some((p) => p.patientId === parentId)) {
      setParentId(selected[0].patientId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected]);

  const childIds = selected
    .map((p) => p.patientId)
    .filter((id): id is string => !!id && id !== parentId);

  const handleConfirm = () => {
    // Defensive guards (Samer PR #97 review 2026-05-14):
    //   - Without a parentId there's nothing to merge into
    //   - Without childIds the mutation would fire with empty payload
    //     (`childPatients: []`) — the button is also disabled in that
    //     state, but this guards against a stale click during a race.
    if (!parentId || childIds.length === 0) return;
    onConfirm(parentId, childIds);
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={`Merge ${selected.length} patients`}
      description="Pick the record that keeps its MRN and history. The others will be
            archived and their data merged into the parent. This can&apos;t be
            undone from this screen — un-merge requires backend support."
      size="md"
      footer={<><Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            // Explicit empty-list guard (Samer PR #97 review): the
            // dialog mounting with `selected.length === 0` keeps
            // parentId null AND childIds empty, so both checks are
            // belt-and-braces but make the disabled-state contract
            // self-evident at the call site.
            disabled={
              isPending ||
              selected.length === 0 ||
              !parentId ||
              childIds.length === 0
            }
          >
            {isPending ? 'Merging…' : `Merge ${childIds.length} into parent`}
          </Button></>}
    >
        

        {/* The design's PaChoiceList: a card per record with the radio dot, the name (+ "Parent" on the chosen one) and
            the person-first facts line. Same selection and disabled rules as before. */}
        <ChoiceList
          label="Parent record"
          selectedId={parentId}
          disabled={isPending}
          onSelect={(id) => {
            const p = selected.find((s) => (s.patientId ?? s.mrn ?? '') === id);
            if (p?.patientId) setParentId(p.patientId);
          }}
          items={selected.map((p) => {
            const fullName =
              p.name ||
              [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ') ||
              'Unnamed patient';
            return {
              id: p.patientId ?? p.mrn ?? fullName,
              primary: fullName,
              badge: 'Parent',
              disabled: !p.patientId,
              secondaryTestId: 'merge-identity-facts',
              secondary: [p.gender?.name || null, p.dob ? `DOB ${p.dob}` : null, p.mrn ? `MRN ${p.mrn}` : 'No MRN']
                .filter(Boolean)
                .join(' · '),
            };
          })}
        />

        
      </Modal>
  );
}
